import {
  Canvas,
  CreateWorkspaceRequest,
  RecentCanvasesByWorkspace,
  UpdateWorkspaceRequest,
  Workspace,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";
import { invalidateArchivedQueries } from "./archived";

export function useWorkspaces(
  options?: Partial<UseQueryOptions<Workspace[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => {
      const list = await orgAPI.workspaces.list();
      return [...list].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
    },
    ...options,
  });
}

export function usePrivateWorkspace() {
  const { data: workspaces } = useWorkspaces();
  return workspaces?.find((ws) => ws.is_private) ?? null;
}

export function useCreateWorkspace(
  options?: UseMutationOptions<Workspace, AxiosError, CreateWorkspaceRequest>,
) {
  const queryClient = useQueryClient();
  return useMutation<Workspace, AxiosError, CreateWorkspaceRequest>({
    mutationFn: (data: CreateWorkspaceRequest) =>
      orgAPI.workspaces.create(data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateWorkspace(
  options?: UseMutationOptions<
    Workspace,
    AxiosError,
    { id: string; data: UpdateWorkspaceRequest }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => orgAPI.workspaces.update(id, data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Moves a workspace (and its canvases, chats, and files) to Trash. */
export function useDeleteWorkspace(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => orgAPI.workspaces.delete(workspaceId),
    ...options,
    onSuccess: (...args) => {
      // Same cache surface as restores: the cascade touches canvases, chats,
      // and files, so every resource list can change.
      invalidateArchivedQueries(queryClient);
      options?.onSuccess?.(...args);
    },
  });
}

export function useWorkspaceById(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error("Workspace ID required");
      return orgAPI.workspaces.getById(workspaceId);
    },
    enabled: !!workspaceId,
    // The workspaces list is effectively always cached already (the rail and
    // the switcher fetch it), so serve its matching entry while the
    // authoritative by-id fetch runs — detail pages then paint their name and
    // icon on the first frame instead of flashing a "Workspace" fallback.
    // Placeholder (not initialData) so it never persists into this cache.
    placeholderData: () =>
      workspaceId
        ? queryClient
            .getQueryData<Workspace[]>(["workspaces"])
            ?.find((ws) => ws.id === workspaceId)
        : undefined,
  });
}

export function useWorkspaceCanvases(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["workspaces", workspaceId, "canvases"],
    queryFn: () => {
      if (!workspaceId) throw new Error("Workspace ID required");
      return orgAPI.workspaces.listCanvases(workspaceId);
    },
    ...options,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
}

/**
 * Fetches up to the 4 most-recently-modified canvases for every workspace the
 * user can view in a single request, returned as a `{ [workspaceId]: Canvas[] }`
 * map. Replaces the per-card `useWorkspaceCanvases` N+1 on the workspaces home.
 */
export function useRecentCanvasesByWorkspace(
  options?: Partial<UseQueryOptions<RecentCanvasesByWorkspace, AxiosError>>,
) {
  return useQuery({
    queryKey: ["workspaces", "recent-canvases-by-workspace"],
    queryFn: () => orgAPI.workspaces.recentCanvasesByWorkspace(),
    ...options,
  });
}

/**
 * TEMPORARY BRIDGE HOOK — delete once the workspace sidebar UI ships.
 *
 * Returns a flat, deduplicated, updated_at-sorted list of every canvas the
 * current user can see:
 *   - canvases from every workspace they are a member of (private + shared)
 *   - canvases directly shared with them ("Shared with Me")
 *
 * This replaces the old flat `GET /canvases` behaviour so the existing
 * WorkspaceIndexPage continues to work without modification.
 */
export function useAllCanvases(
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  const { data: workspaces, isLoading: workspacesLoading } = useWorkspaces();

  // Stable cache key: sorted workspace IDs so order changes don't cause
  // spurious refetches.
  const workspaceIds =
    workspaces
      ?.map((w) => w.id)
      .sort()
      .join(",") ?? "";

  return useQuery({
    queryKey: ["canvases", "all", workspaceIds],
    queryFn: async () => {
      const fetches: Promise<Canvas[]>[] = [
        // Directly shared canvases
        orgAPI.canvas.listSharedWithMe().catch(() => []),
        // All workspace canvases in parallel
        ...(workspaces ?? []).map((ws) =>
          orgAPI.workspaces.listCanvases(ws.id).catch(() => []),
        ),
      ];

      const results = await Promise.all(fetches);
      const all = results.flat();

      // Deduplicate by canvas ID (a canvas could appear in both a workspace
      // and shared-with-me if pruning hasn't run yet).
      const seen = new Set<string>();
      return all
        .filter((c) => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        })
        .sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
    },
    enabled: !workspacesLoading && (options?.enabled ?? true),
    ...options,
  });
}
